import { useCallback } from "react"
import DynamicInput from "../Inputs/DynamicInput"
import useStepNavigation from "../../hook/useStepNavigation"
import { RsvpAnswerDto, RsvpDetails, StepsTranslationsStrings } from "../../models/api/rsvpApiTypes"
import { TranslationsStringsType } from "../../models/enum/rsvpEnum"

interface DynamicStepContentProps {
  buttonColor: string
  backButtonColor: string
  strings: StepsTranslationsStrings[]
  inputType: string | null
  stepName: string
  rsvp: RsvpDetails
  setRsvp: React.Dispatch<React.SetStateAction<RsvpDetails | undefined>>
  stepPipeline: string[]
  setStep: (name: string) => void
}

export default function DynamicStepContent({
  buttonColor,
  backButtonColor,
  strings,
  inputType,
  stepName,
  rsvp,
  setRsvp,
  stepPipeline,
  setStep,
}: DynamicStepContentProps) {
  const { goNext, goPrevious } = useStepNavigation(stepPipeline, stepName, setStep)
  const isBooleanInput = inputType === "Boolean"

  const updateGuestAnswer = useCallback(
    (guestIndex: number, updater: (existingAnswer?: RsvpAnswerDto) => RsvpAnswerDto) => {
      setRsvp((prevRsvp) => {
        if (!prevRsvp) return prevRsvp
        const updatedGuests = prevRsvp.invitation.guests.map((guest, index) => {
          if (index !== guestIndex) return guest
          const currentAnswers = guest.answers ? [...guest.answers] : []
          const answerIndex = currentAnswers.findIndex((answer) => answer.name === stepName)
          if (answerIndex >= 0) {
            currentAnswers[answerIndex] = updater(currentAnswers[answerIndex])
          } else {
            currentAnswers.push(updater(undefined))
          }
          return { ...guest, answers: currentAnswers }
        })
        return {
          ...prevRsvp,
          invitation: { ...prevRsvp.invitation, guests: updatedGuests },
        }
      })
    },
    [setRsvp, stepName]
  )

  return (
    <>
      {isBooleanInput ? (
        <div style={{ display: "flex", justifyContent: "right", color: "#27272E", fontWeight: "bold" }}>
          {strings.find((x) => x.type === TranslationsStringsType.VALUETITLE)?.value}
        </div>
      ) : null}
      {rsvp.invitation.guests.map((guest, index) => {
        const isGuestRelevant =
          guest.rsvp.accepted &&
          (guest.enabledSteps === undefined ||
            guest.enabledSteps.find((enabledStep) => enabledStep.name === stepName)?.enabled === true)

        if (!isGuestRelevant) {
          return null
        }

        return (
          <div
            key={guest.id}
            style={
              isBooleanInput
                ? { display: "flex", flexDirection: "row", alignItems: "center", marginTop: "5px" }
                : { marginTop: "5px" }
            }
          >
            <label className="box-form-label">
              {guest.name} {guest.surname}
            </label>
            <DynamicInput
              inputType={inputType}
              placeholder={strings.find((x) => x.type === TranslationsStringsType.PLACEHOLDER)?.value}
              answer={guest.answers?.find((a) => a.name === stepName)}
              onUpdate={(patch) => {
                updateGuestAnswer(index, (existingAnswer) => ({
                  ...(existingAnswer ?? { name: stepName }),
                  name: stepName,
                  ...patch,
                }))
              }}
            />
          </div>
        )
      })}

      <div style={{ display: "flex", flexDirection: "row" }}>
        <button
          style={{ backgroundColor: backButtonColor, borderColor: backButtonColor }}
          type="button"
          className="btn btn-primary box-button-left"
          onClick={goPrevious}
        >
          {strings.find((x) => x.type === TranslationsStringsType.LEFTBUTTON)?.value}
        </button>
        <button
          type="button"
          className="btn btn-primary box-button-right"
          style={{ backgroundColor: buttonColor, borderColor: buttonColor }}
          onClick={goNext}
        >
          {strings.find((x) => x.type === TranslationsStringsType.MAINBUTTON)?.value}
        </button>
      </div>
    </>
  )
}
